// src/components/HolidayEvent.js
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import LoadingSpinner from "./LoadingSpinner";
import "../index.css";

const API_BASE = process.env.REACT_APP_API_URL;

function getEventNs(data) {
  return safeString(data?.eventNs) || "holiday-event";
}

function claimedCacheKey(eventNs) {
  return `holiday:claimedByDate:${eventNs}`;
}

function loadClaimedCache(eventNs) {
  try {
    const raw = localStorage.getItem(claimedCacheKey(eventNs));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveClaimedCache(eventNs, obj) {
  try {
    localStorage.setItem(claimedCacheKey(eventNs), JSON.stringify(obj || {}));
  } catch {}
}

function isoDayUTC(ms) {
  return new Date(ms).toISOString().slice(0, 10); // YYYY-MM-DD in UTC
}

function formatHMS(totalSeconds) {
  const total = Math.max(0, Math.floor(totalSeconds));
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function formatDHMSCompact(totalSeconds) {
  const total = Math.max(0, Math.floor(totalSeconds));

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  if (days > 0) {
    return `${days}D:${String(hours).padStart(2, "0")}H:${String(minutes).padStart(2, "0")}M`;
  }

  const seconds = total % 60;
  return `${String(hours).padStart(2, "0")}H${String(minutes).padStart(2, "0")}M${String(seconds).padStart(2, "0")}S`;
}

function formatDHMS(totalSeconds) {
  const total = Math.max(0, Math.floor(totalSeconds));

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  return `${days}D ${String(hours).padStart(2, "0")}H ${String(minutes).padStart(2, "0")}M ${String(seconds).padStart(2, "0")}S`;
}

function safeString(x) {
  return (x == null ? "" : String(x)).trim();
}

// Unwrap common backend shapes (inventory row, claim payload wrappers, etc.)
function normalizePrize(p) {
  if (!p) return null;

  // Inventory row: real thing is under `.item`
  if (p.item && (p.user_item_id || p.item_id || p.quantity != null)) return p.item;

  // Claim payload wrappers
  return p.prize || p.reward || p.item || p;
}

// Prefer backend-real names first; only fall back to "Mystery Box" if nothing else exists.
function getPrizeLabel(p) {
  const x = normalizePrize(p);
  return (
    safeString(x?.display_name) ||
    safeString(x?.itemName) || 
    safeString(x?.label) ||
    safeString(x?.name) ||
    safeString(x?.title) ||
    safeString(x?.prize?.display_name) ||
    safeString(x?.prize?.label) ||
    safeString(x?.prize?.name) ||
    safeString(x?.reward?.display_name) ||
    safeString(x?.reward?.label) ||
    safeString(x?.reward?.name) ||
    safeString(x?.item?.display_name) ||
    safeString(x?.item?.label) ||
    safeString(x?.item?.name) ||
    safeString(x?.item?.title) ||
    "Mystery Box"
  );
}

function getPrizeIconSrc(p) {
  const x = normalizePrize(p);

  const t = safeString(
    x?.itemType ||
    x?.type ||
    x?.reward_type ||
    x?.item?.type ||
    x?.prize?.type ||
    x?.reward?.type
  ).toLowerCase();

  const label = safeString(
    x?.display_name ||
    x?.itemName ||
    x?.label ||
    x?.name ||
    x?.title ||
    x?.item?.display_name ||
    x?.item?.name ||
    x?.prize?.display_name ||
    x?.prize?.name ||
    x?.reward?.display_name ||
    x?.reward?.name
  ).toLowerCase();

  const hasCardPiecesAmount =
    Number(x?.card_pieces_amount) > 0 ||
    Number(x?.item?.card_pieces_amount) > 0 ||
    Number(x?.prize?.card_pieces_amount) > 0 ||
    Number(x?.reward?.card_pieces_amount) > 0;

  // FORCE card pieces first, even if backend forgot the exact type
  if (
    t === "card_pieces" ||
    t === "card pieces" ||
    t === "card_piece" ||
    t === "pieces" ||
    label.includes("piece") ||
    label.includes("pieces") ||
    hasCardPiecesAmount
  ) {
    return "/pieces.png";
  }

  if (t === "runes") return "/runes.png";
  if (t === "sparks") return "/sparks.png";
  if (t === "booster") return "/energy.png";
  if (t === "seasonal_accessory") return "/fragment.png";
  if (t === "ability") return "/fightlog.png";
  if (t === "card") return "/common.png";
  if (t === "evolve") return "/evolveicon.png";
  if (t === "spins") return "/fortuneicon.png";

  const direct =
    safeString(x?.icon) ||
    safeString(x?.icon_url) ||
    safeString(x?.image) ||
    safeString(x?.image_url) ||
    safeString(x?.img) ||
    safeString(x?.img_url) ||
    safeString(x?.prize?.icon) ||
    safeString(x?.prize?.icon_url) ||
    safeString(x?.prize?.image) ||
    safeString(x?.prize?.image_url) ||
    safeString(x?.reward?.icon) ||
    safeString(x?.reward?.icon_url) ||
    safeString(x?.reward?.image) ||
    safeString(x?.reward?.image_url) ||
    safeString(x?.item?.icon) ||
    safeString(x?.item?.icon_url) ||
    safeString(x?.item?.image) ||
    safeString(x?.item?.image_url);

  if (direct) return direct;

  return "/pack.png";
}

function getPrizeType(p) {
  const x = normalizePrize(p);
  return safeString(
    x?.itemType || x?.type || x?.reward_type || x?.item?.type || x?.prize?.type || x?.reward?.type
  ).toLowerCase();
}

function isBalancePrize(p) {
  const t = getPrizeType(p);
  return t === "runes" || t === "sparks";
}

/**
 * Determines when the event ends.
 * Priority:
 * 1) backend-provided timestamps/dates (endAt / endsAt / endDate / eventEnd / eventEndsAt)
 * 2) max prize date + 1 day at 00:00
 *
 * Returns ms timestamp, or null if cannot be determined.
 */
function getEventEndMs(data, prizes) {
  const raw =
    data?.endAt ||
    data?.endsAt ||
    data?.eventEnd ||
    data?.eventEndsAt ||
    data?.endDate ||
    data?.endsOn ||
    null;

  if (raw) {
    const s = safeString(raw);
    // YYYY-MM-DD: treat as end of that day
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return Date.parse(`${s}T23:59:59Z`);

    const ms = Date.parse(s);
    if (!Number.isNaN(ms)) return ms;
  }

  if (Array.isArray(prizes) && prizes.length) {
    const last = prizes
      .map((p) => safeString(p?.date))
      .filter(Boolean)
      .sort()
      .slice(-1)[0];

    if (last) {
      const nextMs = Date.parse(`${last}T00:00:00Z`) + 24 * 3600 * 1000;
      if (!Number.isNaN(nextMs)) return nextMs;
    }
  }

  return null;}

export default function HolidayEvent({ user, setUser, onNavigate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [tick, setTick] = useState(0);

  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [claimedPrize, setClaimedPrize] = useState(null);
  // Local per-date overrides so the grid can show the REAL reward immediately after a claim
  const [claimedByDate, setClaimedByDate] = useState({});

  const token = useMemo(() => localStorage.getItem("token"), []);

  const load = async ({ silent = false } = {}) => {
    try {
      setError("");
      if (!silent) setLoading(true);

      const res = await fetch(`${API_BASE}/api/holiday-event/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to load");
      setData(json);
      const eventNs = getEventNs(json);
setClaimedByDate((prev) => {
  // don't clobber in-memory updates; merge cache under them
  const cached = loadClaimedCache(eventNs);
  return { ...cached, ...prev };
});
      return json;
    } catch (e) {
      setError(e?.message || "Failed");
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const claimToday = async () => {
  if (saving) return;

  try {
    setSaving(true);
    setError("");

    const serverNowMs0 = Date.parse(data?.serverNow || "") || Date.now();
    const nowMs = serverNowMs0 + tick * 1000;
    const today = isoDayUTC(nowMs);

    const headers = { Authorization: `Bearer ${token}` };

    const res = await fetch(`${API_BASE}/api/holiday-event/claim`, {
      method: "POST",
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || "Claim failed");

    const claimedFromResponse =
      json?.granted ||
      json?.prize ||
      json?.reward ||
      json?.claimedPrize ||
      json?.claimed ||
      json?.item ||
      json?.data?.prize ||
      json?.data?.reward ||
      null;

    const statusJson = await load({ silent: true });

    const fallbackFromStatus =
      (statusJson?.prizes || []).find((p) => String(p?.date) === String(today)) || null;

    const finalPrize = {
  ...(fallbackFromStatus || {}),
  ...(claimedFromResponse || {}),
};

    setClaimedPrize(finalPrize);

    setClaimedByDate((prev) => {
      const next = { ...prev, [String(today)]: finalPrize };
      const eventNs = getEventNs(statusJson || data);
      saveClaimedCache(eventNs, next);
      return next;
    });

    setShowPrizeModal(true);

    if (json?.user && typeof setUser === "function") {
      setUser((prev) => ({ ...prev, ...json.user }));
    }
  } catch (e) {
    setError(e?.message || "Claim failed");
  } finally {
    setSaving(false);
  }
};

  if (loading && !data) {
  return (
    <div
      className="shop-container"
      style={{
        paddingTop: 0,
        paddingBottom: 0,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 450,
          minHeight: 430,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingSpinner
          text="Loading Holiday Event"
          size={64}
        />
      </div>
    </div>
  );
}

  if (error) {
    return (
      <div style={{ padding: 16, color: "salmon", textShadow: "0 1px 2px #000" }}>
        {error}
      </div>
    );
  }

  const eventName =
    safeString(data?.eventName) || safeString(data?.name) || safeString(data?.title) || "🎁 Special Event";

  if (!data?.enabled) {
    return (
      <div style={{ padding: 16, color: "#ccc", textShadow: "0 1px 2px #000" }}>
        <h2 style={{ marginTop: 0 }}>{eventName}</h2>
        <div>No active event right now.</div>
      </div>
    );
  }

const serverNowMs0 = Date.parse(data?.serverNow || "") || Date.now();
const nowMs = serverNowMs0 + tick * 1000;

const today = isoDayUTC(nowMs);

  const prizes = Array.isArray(data?.prizes)
    ? data.prizes.slice().sort((a, b) => String(a?.date || "").localeCompare(String(b?.date || "")))
    : [];

  const eventEndMs = getEventEndMs(data, prizes);
  const eventEndsInSec =
    eventEndMs != null ? Math.max(0, Math.floor((eventEndMs - nowMs) / 1000)) : null;

  const entryCost = Number(data?.entryCostRunes || 0);

  const modalPrize = normalizePrize(claimedPrize) || claimedPrize;
  const modalAddsToBalance = isBalancePrize(modalPrize);

  return (
    <>
      <div
        className="shop-container"
        style={{
          fontFamily: '"Fredoka", sans-serif',
          fontWeight: 600
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", padding: "10px", paddingTop: "0px" }}>
          <div style={{ width: "100%", maxWidth: 550 }}>
            <div className="shadow1" style={{ color: "#ffd877", fontSize: 21, margin: 0, marginBottom: 5 }}>
              Holiday Week
            </div>
            <div className="shadow2" style={{ color: "#fff", fontSize: 28, margin: 0, marginBottom: 15 }}>
              {eventName}
            </div>

            {eventEndsInSec != null && (

              <div style={{width: "100%"}}>
  <div
    style={{
      maxWidth: 300,
      margin: "0 auto 20px",
      fontSize: 15,
      color: eventEndsInSec > 0 ? "#ddd" : "#ffb3b3",
      textShadow: "0 1px 2px #000",
      backgroundColor: "rgba(0,0,0,.3)",
      height: "42px",
      lineHeight: "42px",
      borderRadius: "6px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "4px"
    }}
  >
    {eventEndsInSec > 0 ? (
      <>
        <img src="./eventclock.png" style={{height: "28px"}}/><span style={{ color: "#ffd877" }}>Event Ends In:</span>
        <span>{formatDHMS(eventEndsInSec)}</span>
      </>
    ) : (
      "Event Ended"
    )}
  </div></div>
)}


            {(entryCost > 0 || data?.entered != null) && (
              <div
                style={{
                  marginBottom: 12,
                  fontSize: 13,
                  color: "#ddd",
                  textShadow: "0 1px 2px #000",
                  opacity: 0.95,
                  lineHeight: 1.35,
                }}
              >
                {entryCost > 0 && (
                  <div>
                    Entry cost: <b>{entryCost}</b> runes{" "}
                    {data?.entered ? <span style={{ color: "#9effa8" }}>✅</span> : ""}
                  </div>
                )}
              </div>
            )}

            <div style={{marginLeft: -20, marginRight: -20}}>
            <div className="modal_title">Mistery Boxes</div>

            <div className="modal_body modal_body_2">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
                paddingBottom: 5,
              }}
            >
              {prizes.map((p, idx) => {
                const date = String(p?.date || "");
                const isToday = date === String(today);
                const isPast = date && date < String(today);
                const isFuture = date && date > String(today);

                const isClaimed = !!p?.claimed || !!claimedByDate?.[date];
const resolvedPrize = isClaimed ? (claimedByDate?.[date] || p) : p;

const label = isClaimed ? getPrizeLabel(resolvedPrize) : "Mystery Box";
const iconSrc = isClaimed
  ? (
      safeString(getPrizeLabel(resolvedPrize)).toLowerCase() === "card pieces"
        ? "/pieces.png"
        : getPrizeIconSrc(resolvedPrize)
    )
  : "/tlocked.png";

                const untilMs = isFuture ? Math.max(0, Date.parse(`${date}T00:00:00Z`) - nowMs) : 0;

                const tileClickable = isToday && !isClaimed && !saving;

                const baseBtnStyle = {
                  position: "absolute",
                  left: 10,
                  right: 10,
                  bottom: -18,
                  fontSize: 11,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                };

                let button = null;

                if (isToday && !isClaimed) {
                  button = (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        claimToday();
                      }}
                      disabled={saving}
                      className="game-button-small green-button"
                      style={{
                        ...baseBtnStyle,
                        cursor: saving ? "not-allowed" : "pointer",
                        color: "#fff",
                        opacity: saving ? 0.7 : 1,
                        fontSize: "10px",
                      }}
                    >
                      {saving ? "Grabbing" : "Grab It"}
                    </button>
                  );
                }

                if (isClaimed) {
                  button = (
                    <button
                    className="game-button-small green-button"
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      disabled
                      style={{
                        ...baseBtnStyle,
                        borderRadius: 5,
                        cursor: "not-allowed",
                        fontSize: "10px",
                      }}
                    >
                      Grabbed
                    </button>
                  );
                }

                if (!isClaimed && isPast) {
                  button = (
                    <button
                    className="game-button-small red-button"
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      disabled
                      style={{
                        ...baseBtnStyle,
                        cursor: "not-allowed",
                        fontSize: "10px",
                      }}
                    >
                      Expired
                    </button>
                  );
                }

                if (!isClaimed && isFuture) {
                  button = (
                    <button
                    className="game-button-small yellow-button"
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      disabled
                      style={{
                        ...baseBtnStyle,
                        width: "100%",
                        marginLeft: -10,
                        cursor: "not-allowed",
                        fontSize: "12px",
                      }}
                      title={`Available in ${formatDHMS(Math.floor(untilMs / 1000))}`}
                    >
                      {formatDHMSCompact(Math.floor(untilMs / 1000))}
                    </button>
                  );
                }

                return (
                  <div
                    key={date || idx}
                    onClick={() => {
                      if (tileClickable) claimToday();
                    }}
                    style={{
                      position: "relative",
                      paddingTop: "115%",
                      marginBottom: 10,
                      backgroundImage: 'url("/v2/item.png")',
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "100% 100%",
                      backgroundPosition: "center",
                      cursor: tileClickable ? "pointer" : "default",
                      opacity: isClaimed || isPast ? 0.6 : 1,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 6,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        padding: 10,
                        paddingTop: 20,
                        textAlign: "center",
                      }}
                    >
                      {iconSrc && (
                        <img
                          src={iconSrc}
                          alt={label}
                          style={{
                            width: "auto",
                            height: "50%",
                            objectFit: "contain",
                            filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.7))",
                            marginBottom: 8,
                            marginTop: -10,
                          }}
                        />
                      )}

                      <div
  style={{
    fontWeight: 600,
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.9)",
    lineHeight: 1.15,
    fontSize: "14px",

    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    textAlign: "center",

    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  }}
>
  {label}
</div>


                      {button}
                    </div>
                  </div>
                );
              })}
            </div></div>
          </div></div>
        </div>
      </div>

      {showPrizeModal && claimedPrize && (
        <div className="modal_box">
        <div className="modal_title">WooHoo!<button onClick={() => {
                  setShowPrizeModal(false);
                  setClaimedPrize(null);
                }} className="modal_close"/></div>
          <motion.div className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
          <div className="modal_inside">
            <div className="shadow2"
              style={{
                marginBottom: "10px",
                fontWeight: 600,
                fontSize: 28
              }}
            >
              Reward Claimed!
            </div>

            <div className="info"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
                textAlign: "center",
              }}
            >
              {getPrizeIconSrc(modalPrize) && (
                <img
                  src={getPrizeIconSrc(modalPrize)}
                  alt={getPrizeLabel(modalPrize)}
                  style={{ width: 64, height: 64 }}
                />
              )}

              <p
                style={{
                  fontSize: 18,
                  color: "#fff",
                  textShadow: "0 1px 2px #000",
                  margin: 0,
                  fontWeight: 800,
                }}
              >
                {getPrizeLabel(modalPrize)}
              </p>
                            <p
                style={{
                  fontSize: 14,
                  color: "#ffd877",
                  textShadow: "0 1px 2px #000",
                  margin: 0,
                  lineHeight: 1.35,
                }}
              >
                {modalAddsToBalance
                  ? "This prize was added to your balance."
                  : "This prize was added to your inventory."}
              </p>
            </div></div>

            <div className="modal_footer">
              {!modalAddsToBalance && (
                <button

                className="game-button green-button" style={{marginRight: 10}}
                  onClick={() => {
                    setShowPrizeModal(false);
                    setClaimedPrize(null);
                    onNavigate?.("inventoryHub");
                  }}
                >
                  Inventory
                </button>
              )}

              <button className="game-button red-button"
                onClick={() => {
                  setShowPrizeModal(false);
                  setClaimedPrize(null);
                }}
              >
                CLOSE
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
